import { useState, useEffect } from 'react';

interface HeaderProps {
  isScrolled: boolean;
}

const navLinks = [
  { label: 'About Valaxis Group', id: 'about-valaxis' },
  { label: 'Investment Thesis', id: 'investment-focus' },
  { label: 'For Business Owners', id: 'for-business-owners' },
  { label: 'For Investors', id: 'for-investors' },
  { label: 'Contact', id: 'contact' },
];

export default function Header({ isScrolled }: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    setMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  // Close menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const bgStyle = isScrolled || menuOpen
    ? 'backdrop-blur-[16px]'
    : 'bg-transparent';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${bgStyle}`}
      style={{
        borderBottom: isScrolled || menuOpen ? '1px solid rgba(0,0,0,0.08)' : 'none',
        backgroundColor: isScrolled || menuOpen ? 'rgba(248,250,252,0.97)' : 'transparent',
      }}
    >
      {/* Main bar */}
      <div className="max-w-[1120px] mx-auto px-5 md:px-10 h-[100px] md:h-[88px] flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center h-full py-2 overflow-hidden">
          <img
            src="https://static.readdy.ai/image/cd71ffc29b4128737d19646fb970e806/23c6485507286e2714b90e8bd3ae7df8.png"
            alt="Valaxis Group"
            className="block cursor-pointer h-[208px] md:h-[267px] w-auto object-contain object-left -ml-[48px] md:-ml-[60px]"
            onClick={() => { setMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          />
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6 lg:gap-7">
          {navLinks.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              onClick={(e) => { e.preventDefault(); scrollToSection(link.id); }}
              className="text-[15px] text-[#5F6670] hover:text-[#1E2430] transition-colors cursor-pointer whitespace-nowrap font-medium"
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Hamburger button — mobile only */}
        <button
          className="md:hidden flex items-center justify-center w-10 h-10 rounded-md cursor-pointer text-[#1E2430]"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          <i className={`text-[22px] ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
        </button>
      </div>

      {/* Mobile dropdown menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-[rgba(30,36,48,0.08)] bg-[rgba(248,250,252,0.97)]">
          <nav className="flex flex-col px-5 py-4 gap-1">
            {navLinks.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                onClick={(e) => { e.preventDefault(); scrollToSection(link.id); }}
                className="text-[16px] font-medium text-[#1E2430] py-3 border-b border-[rgba(30,36,48,0.06)] last:border-0 cursor-pointer"
              >
                {link.label}
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
